<template>
  <nav v-if="visible" class="hb-bottom-nav" role="navigation" aria-label="Employee primary navigation">
    <button
      v-for="tab in tabs"
      :key="tab.id"
      type="button"
      class="hb-bn-item"
      :class="{ active: isTabActive(tab) }"
      @click.stop.prevent="onTabClick(tab)"
      @touchend.stop.prevent="onTabClick(tab)"
      :aria-current="isTabActive(tab) ? 'page' : null"
    >
      <span class="hb-bn-icon-wrap">
        <i :class="tab.icon"></i>
        <span v-if="tab.badge" class="hb-bn-badge">{{ tab.badge > 99 ? '99+' : tab.badge }}</span>
      </span>
      <span class="hb-bn-label">{{ tab.label }}</span>
    </button>
  </nav>
</template>

<script>
/* eslint-disable */
export default {
  name: "MobileBottomNav",
  props: {
    notificationCount: { type: Number, default: 0 }
  },
  data() {
    return {
      isMobile: false,
      isEmployee: false,
      resizeHandler: null,
      tabs: [
        { id: "home", label: "Home", icon: "bx bx-home-alt", route: "/employee-dashboard", match: ["/employee-dashboard"] },
        { id: "attendance", label: "Clock", icon: "bx bx-time-five", route: "/employee-attendance-clock", match: ["/employee-attendance"] },
        { id: "leave", label: "Leave", icon: "bx bx-calendar-check", route: "/employee-leave-apply", match: ["/employee-leave", "/employee-off-day"] },
        { id: "profile", label: "Me", icon: "bx bx-user", route: "/employee-profile", match: ["/employee-profile"] },
        { id: "more", label: "More", icon: "bx bx-menu", route: null, match: [] }
      ]
    };
  },
  computed: {
    visible() {
      return this.isMobile && this.isEmployee;
    }
  },
  mounted() {
    this.detectMobile();
    this.detectEmployee();
    this.resizeHandler = () => {
      this.detectMobile();
      this.updateBodyClass();
    };
    window.addEventListener("resize", this.resizeHandler);
    window.addEventListener("storage", this.detectEmployee);
    this.$nextTick(this.updateBodyClass);
  },
  beforeUnmount() {
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler);
    window.removeEventListener("storage", this.detectEmployee);
    document.body.classList.remove("hb-bottom-nav-visible");
  },
  watch: {
    visible() { this.$nextTick(this.updateBodyClass); },
    $route() { this.detectEmployee(); }
  },
  methods: {
    detectMobile() {
      this.isMobile = window.matchMedia("(max-width: 768px)").matches;
    },
    detectEmployee() {
      try {
        const raw = sessionStorage.getItem("userInfo");
        if (!raw) { this.isEmployee = false; return; }
        const info = JSON.parse(raw);
        this.isEmployee = (info?.category || "").toUpperCase() === "EMPLOYEES";
      } catch (_) { this.isEmployee = false; }
    },
    updateBodyClass() {
      if (this.visible) {
        document.body.classList.add("hb-bottom-nav-visible");
      } else {
        document.body.classList.remove("hb-bottom-nav-visible");
      }
    },
    isTabActive(tab) {
      const path = (this.$route && this.$route.path) || "";
      if (tab.route && path === tab.route) return true;
      return tab.match.some((p) => path.startsWith(p));
    },
    onTabClick(tab) {
      if (tab.id === "more") {
        this.openSideDrawer();
        return;
      }
      if (tab.route && this.$route.path !== tab.route) {
        this.$router.push(tab.route).catch(() => {});
      }
    },
    openSideDrawer() {
      try { window.dispatchEvent(new CustomEvent("hb-open-mobile-menu")); } catch (_) {}
      try { document.dispatchEvent(new CustomEvent("hb-open-mobile-menu")); } catch (_) {}
    }
  }
};
</script>

<style scoped>
.hb-bottom-nav {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  /* Above the side-drawer backdrop (998) and the side nav itself (1000)
     so tabs are always tappable. Modal overlays still sit on top via
     their own higher z-index. */
  z-index: 1001;
  display: flex;
  justify-content: space-around;
  align-items: stretch;
  /* Solid fallback (no backdrop-filter on the parent — iOS Safari has a
     known bug where it can swallow taps on buttons inside an element
     with backdrop-filter). The frosted-glass look is achieved on a
     ::before pseudo behind the buttons instead. */
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -6px 18px rgba(15, 23, 42, 0.08);
  padding:
    6px
    calc(4px + env(safe-area-inset-right))
    calc(6px + env(safe-area-inset-bottom))
    calc(4px + env(safe-area-inset-left));
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
  isolation: isolate;
}

.hb-bottom-nav::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  backdrop-filter: saturate(180%) blur(14px);
  z-index: -1;
  pointer-events: none;
}

.hb-bn-item {
  flex: 1 1 0;
  min-width: 0;
  border: none;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 6px 4px;
  color: #64748b;
  cursor: pointer;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.01em;
  position: relative;
  z-index: 1;
  pointer-events: auto;
  -webkit-tap-highlight-color: transparent;
  -webkit-appearance: none;
  appearance: none;
  touch-action: manipulation;
  transition: color 0.18s ease, transform 0.18s ease;
}

.hb-bn-item:active { transform: scale(0.94); }

.hb-bn-icon-wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  transition: background 0.18s ease, color 0.18s ease, transform 0.18s ease;
  font-size: 22px;
}

.hb-bn-badge {
  position: absolute;
  top: -4px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #dc2626;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 0 2px #ffffff;
}

.hb-bn-label {
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.hb-bn-item.active {
  color: #2195d3;
}

.hb-bn-item.active .hb-bn-icon-wrap {
  background: linear-gradient(135deg, #2195d3 0%, #0f6899 100%);
  color: #ffffff;
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(33, 149, 211, 0.32);
}

[data-theme="dark"] .hb-bottom-nav {
  background: rgba(8, 45, 66, 0.97);
  border-top-color: #1478b0;
  box-shadow: 0 -6px 18px rgba(0, 0, 0, 0.45);
}

[data-theme="dark"] .hb-bn-item { color: #6dc0e9; }
[data-theme="dark"] .hb-bn-item.active { color: #ffffff; }
[data-theme="dark"] .hb-bn-badge { box-shadow: 0 0 0 2px #082d42; }
</style>
